import { useState } from "react";
import { api, fmtTRY, GROUP_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, CreditCard, Banknote, Plus, Trash2 } from "lucide-react";

const groupIcons = { banka: Building2, pos: CreditCard, nakit: Banknote };
const groups = ["banka", "pos", "nakit"];

export default function AccountsSection({ accounts, onChange, canEdit = false }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [group, setGroup] = useState("banka");
  const [bankName, setBankName] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Hesap adı girin");
    setSaving(true);
    try {
      await api.post("/accounts", {
        name: name.trim(),
        group,
        bank_name: bankName.trim() || null,
      });
      toast.success("Hesap oluşturuldu");
      setName("");
      setBankName("");
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Hesap silindi");
      onChange();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Banka, POS ve Nakit hesaplarınızı yönetin.
        </p>
        {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-new-account-btn" className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="h-4 w-4" /> Yeni Hesap
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="new-account-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Yeni Hesap Ekle</DialogTitle>
              <DialogDescription>
                Banka, POS veya Nakit için yeni bir hesap oluşturun.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Hesap Türü</Label>
                <Select value={group} onValueChange={setGroup}>
                  <SelectTrigger data-testid="account-group-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="banka">Banka Hesabı</SelectItem>
                    <SelectItem value="pos">POS Hesabı</SelectItem>
                    <SelectItem value="nakit">Nakit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Hesap Adı</Label>
                <Input
                  data-testid="account-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Örn. Ana Kasa, Vadesiz TL"
                />
              </div>
              {group !== "nakit" && (
                <div className="space-y-1.5">
                  <Label>Banka Adı (opsiyonel)</Label>
                  <Input
                    data-testid="account-bank-input"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Örn. Ziraat, Garanti, İş Bankası"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                data-testid="save-account-btn"
                onClick={create}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
              >
                {saving ? "Kaydediliyor..." : "Hesabı Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {groups.map((g) => {
        const items = accounts.filter((a) => a.group === g);
        const Icon = groupIcons[g];
        return (
          <div key={g} data-testid={`account-group-${g}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                {GROUP_LABELS[g]}
              </h3>
            </div>
            {items.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-xl p-6 text-sm text-slate-400">
                Bu grupta henüz hesap yok.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`account-card-${a.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{a.name}</p>
                        {a.bank_name && (
                          <p className="text-xs text-slate-400 mt-0.5">{a.bank_name}</p>
                        )}
                      </div>
                      {canEdit && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            data-testid={`delete-account-${a.id}`}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50 -mr-1 -mt-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hesabı sil?</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{a.name}" hesabı ve bu hesaba ait tüm işlemler kalıcı olarak silinecek.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`confirm-delete-account-${a.id}`}
                              onClick={() => remove(a.id)}
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Sil
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                    <p className="mt-4 text-xl font-bold tracking-tight text-slate-900 tabular-nums">
                      {fmtTRY(a.balance)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
